import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { FiShield, FiArrowLeft, FiKey } from 'react-icons/fi';
import logo from '../assets/logo-auth.png';
import './Auth.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [show2fa, setShow2fa] = useState(false);
  const [challengeToken, setChallengeToken] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [tfaError, setTfaError] = useState('');

  const { login, verify2faLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.requires2fa) {
        // 2FA required - show verification screen
        setChallengeToken(result.challengeToken);
        setShow2fa(true);
        setLoading(false);
        return;
      }

      // Login successful (no 2FA)
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2fa = async (e) => {
    e.preventDefault();
    setTfaError('');

    if (!verifyCode) {
      setTfaError('Please enter a verification code');
      return;
    }

    setLoading(true);

    try {
      const result = await verify2faLogin(challengeToken, verifyCode, useRecoveryCode);

      if (result.warning) {
        toast.warning(result.warning);
      }

      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      const msg = error.response?.data?.message || 'Verification failed';
      if (error.response?.status === 401 && msg.toLowerCase().includes('expired')) {
        toast.error('Session expired. Please log in again.');
        resetToLogin();
      } else {
        // Stay on OTP screen, show inline error, clear code for retry
        setTfaError(msg === 'Verification failed' ? 'Invalid code. Please try again.' : msg);
        setVerifyCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetToLogin = () => {
    setShow2fa(false);
    setChallengeToken('');
    setVerifyCode('');
    setUseRecoveryCode(false);
    setPassword('');
  };

  // 2FA Verification Screen
  if (show2fa) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="tfa-icon">
              <FiShield size={48} color="#4A90E2" />
            </div>
            <h1>Two-Factor Authentication</h1>
            <p className="tagline">
              {useRecoveryCode
                ? 'Enter a recovery code'
                : 'Enter the 6-digit code from your authenticator app'}
            </p>
          </div>

          <form onSubmit={handleVerify2fa}>
            {tfaError && (
              <div className="auth-error">
                {tfaError}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="verifyCode">
                {useRecoveryCode ? 'Recovery Code' : 'Verification Code'}
              </label>
              <input
                type="text"
                id="verifyCode"
                value={verifyCode}
                onChange={(e) => {
                  setTfaError('');
                  setVerifyCode(
                    useRecoveryCode
                      ? e.target.value.toUpperCase()
                      : e.target.value.replace(/\D/g, '').slice(0, 6)
                  );
                }}
                required
                placeholder={useRecoveryCode ? 'XXXX-XXXX' : '000000'}
                autoFocus
                autoComplete="one-time-code"
                className={tfaError ? 'input-error' : ''}
                style={useRecoveryCode ? {} : {
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  letterSpacing: '0.3rem',
                }}
              />
            </div>

            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </form>

          <div className="auth-footer" style={{ marginTop: 24 }}>
            <button
              type="button"
              className="link-button"
              onClick={() => {
                setUseRecoveryCode(!useRecoveryCode);
                setVerifyCode('');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#3498DB',
                cursor: 'pointer',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                width: '100%',
              }}
            >
              <FiKey size={14} />
              {useRecoveryCode ? 'Use authenticator code instead' : 'Use a recovery code instead'}
            </button>

            <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #ecf0f1' }} />

            <button
              type="button"
              onClick={resetToLogin}
              style={{
                background: 'none',
                border: 'none',
                color: '#7F8C8D',
                cursor: 'pointer',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                width: '100%',
              }}
            >
              <FiArrowLeft size={14} />
              Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Standard Login Screen
  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img src={logo} alt="Hisab OK Hai" className="auth-logo" />
          <h1>Hisab OK Hai</h1>
          <p className="tagline">Roz ka kharcha, bina tension</p>
        </div>

        <h2>Login</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
            />
          </div>

          <div className="forgot-password-link" style={{ textAlign: 'right', marginBottom: 16 }}>
            <Link to="/forgot-password" style={{ fontSize: '0.9rem', color: '#3498DB' }}>
              Forgot Password?
            </Link>
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Don't have an account? <Link to="/signup">Sign up</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
